/*
  Lots of duplicate code as the PlayerInput component.
  Should refactor to consolidate!
*/
import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Container,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Stack } from '@mui/system';
import { Penalty, Team } from '@par/shared/types/game';
import { Mode, Player } from '@par/shared/common/types';
import { CorrectAnswer } from '../CorrectAnswer';
import { Inputter } from './Inputter';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import './styles.css';
import { Socket } from 'socket.io-client';
import { resizeImage } from '../../../../api/cloudinary';

type PropTypes = {
  answer?: string[][];
  deselectPlayer: (profileId: string) => void;
  fuzzy?: Record<string, number>;
  isAdmin: boolean;
  league?: boolean;
  liveInputs?: Record<string, string>;
  mode: Mode;
  myTeam?: Team;
  otherTeam?: Team;
  penalty?: Penalty;
  players: Record<string, Player>;
  questionIndex: number;
  selectedProfileIds?: string[];
  socket?: Socket;
  timerLimitReached: boolean;
};

export const SpectatePlayerInput = ({
  answer,
  deselectPlayer,
  fuzzy,
  isAdmin,
  league,
  liveInputs,
  mode,
  myTeam,
  otherTeam,
  penalty,
  players,
  questionIndex,
  selectedProfileIds,
  socket,
  timerLimitReached,
}: PropTypes) => {
  const [selectedPlayers, setSelectedPlayers] = useState<Player[]>();
  const [liveInputCache, setLiveInputCache] = useState<Record<string, string>>(
    {}
  );
  const [FB, setFB] = useState<Record<string, string>>({});
  const [selectedPlayersLocked, setSelectedPlayersLocked] =
    useState<Record<string, boolean>>();
  const [myTeamPlayers, setMyTeamPlayers] = useState<Player[]>();
  const [otherTeamPlayers, setOtherTeamPlayers] = useState<Player[]>();

  const theme = useTheme();
  const xsBreakpoint = useMediaQuery(theme.breakpoints.only('xs'));

  useEffect(() => {
    if (isAdmin && socket) {
      socket.on('input-f', (username: string) =>
        updateFB('f', username)
      );
      socket.on('input-b', (username: string) =>
        updateFB('b', username)
      );
    }
  }, []);

  useEffect(() => {
    if (!penalty) return;
    setSelectedPlayersLocked((prev) => ({
      ...prev,
      [penalty.profileId]: true,
    }));
    setTimeout(() => {
      setSelectedPlayersLocked((prev) => ({
        ...prev,
        [penalty.profileId]: false,
      }));
    }, 1000);
  }, [penalty]);

  useEffect(() => {
    const filteredPlayers = selectedProfileIds?.map((id) => players[id]);
    setSelectedPlayers(filteredPlayers);
  }, [selectedProfileIds, players]);

  useEffect(() => {
    setLiveInputCache({});
  }, [questionIndex]);

  useEffect(() => {
    if (!myTeam || !myTeam.members) return;
    const updateMyTeamPlayers = Object.values(players).filter((p) =>
      myTeam.members.includes(p.profileId)
    );
    setMyTeamPlayers(updateMyTeamPlayers);
  }, [myTeam]);

  useEffect(() => {
    if (!otherTeam || !otherTeam.members) return;
    const updateOtherTeamPlayers = Object.values(players).filter((p) =>
      otherTeam.members.includes(p.profileId)
    );
    setOtherTeamPlayers(updateOtherTeamPlayers);
  }, [otherTeam]);

  const updateFB = (type: 'f' | 'b', username: string) => {
    FB[username] = type;
    setFB(FB);
  };

  const updateLiveInputCache = (liveInput: Record<string, string>) => {
    liveInputCache[liveInput.profileId] = liveInput.input;
    setLiveInputCache(liveInputCache);
  };

  const sitAndGoInputDisplay = (p: Player) => (
    <Stack
      direction="row"
      spacing={2}
      marginTop={2}
      sx={{ textAlign: 'left', width: '100%' }}
      alignItems={'center'}
    >
      {mode !== 'teams' ? (
        <IconButton
          onClick={() => deselectPlayer(p.profileId)}
          sx={{
            height: '40px',
            opacity: 0.5,
            '&:hover': {
              opacity: 1,
              color: 'error.main',
            },
          }}
        >
          <CloseOutlinedIcon />
        </IconButton>
      ) : null}
      <Tooltip title={p.username} placement="right-start">
        <Avatar
          src={resizeImage(p.avatarPublicId, 50, 50)}
          variant="rounded"
          sx={{
            height: '40px',
            width: '40px',
            position: 'relative',
            filter: isAdmin && FB[p.username] === 'b' ? 'blur(0.2rem)' : '',
            // top: '50%',
            // transform: 'translateY(-50%)',
          }}
        >
          {!p.avatarUrl ? p.username.charAt(0) : null}
        </Avatar>
      </Tooltip>
      <Stack direction="row" spacing={1}>
        <Inputter
          currentLiveInput={liveInputs?.[p.profileId]}
          fuzzyScore={fuzzy?.[p.profileId]}
          inputPlaceholder={p.username}
          liveInputCache={liveInputCache}
          mode={mode}
          player={p}
          locked={selectedPlayersLocked?.[p.profileId]}
          questionIndex={questionIndex}
          socket={socket}
          spectate={true}
          timerLimitReached={timerLimitReached}
          updateLiveInputCache={updateLiveInputCache}
        ></Inputter>
        <CorrectAnswer
          abbreviateAves={
            (selectedPlayers && selectedPlayers?.length > 4) || mode === 'teams'
          }
          mode={mode}
          player={p}
          smallFont
          spectate={true}
        ></CorrectAnswer>
      </Stack>
    </Stack>
  );

  return (
    <>
      {mode === 'sit-n-go' ? (
        <>
          {selectedPlayers && selectedPlayers?.length <= 4 ? (
            <Stack spacing={2} width="50%">
              {selectedPlayers?.map((p) => sitAndGoInputDisplay(p))}
            </Stack>
          ) : (
            <Stack direction={'row'} width="100%">
              <Stack spacing={2} width="50%">
                {selectedPlayers
                  ?.slice(0, 4)
                  .map((p) => sitAndGoInputDisplay(p))}
              </Stack>
              <Stack spacing={2} width="50%">
                {selectedPlayers
                  ?.slice(4, 8)
                  .map((p) => sitAndGoInputDisplay(p))}
              </Stack>
            </Stack>
          )}
        </>
      ) : mode === 'teams' ? (
        <Container
          sx={{
            position: 'absolute',
            top: 10,
          }}
        >
          <Stack
            direction={'row'}
            width="100%"
            justifyContent={'space-between'}
            paddingX={5}
          >
            <Stack spacing={3} width="40%">
              <Typography variant="h6led" textAlign={'center'} fontWeight={600}>
                Team {myTeam?.username}
              </Typography>
              <Stack spacing={1}>
                {myTeamPlayers?.map((p) => {
                  // since "players" gets updated as players answer correctly,
                  // after we find the teams, we need to use players for rendering
                  const player = players[p.profileId];
                  return sitAndGoInputDisplay(player);
                })}
              </Stack>
            </Stack>
            {!league || isAdmin ? (
              <Stack spacing={3} width="40%">
                <Typography
                  variant="h6led"
                  textAlign={'center'}
                  fontWeight={600}
                >
                  Team {otherTeam?.username}
                </Typography>
                <Stack spacing={1}>
                  {otherTeamPlayers?.map((p) => {
                    // since "players" gets updated as players answer correctly,
                    // after we find the teams, we need to use players for rendering
                    const player = players[p.profileId];
                    return sitAndGoInputDisplay(player);
                  })}{' '}
                </Stack>
              </Stack>
            ) : null}
          </Stack>
        </Container>
      ) : (
        <Stack direction={'row'} spacing={10}>
          {selectedPlayers?.map((p, i) => (
            <Stack direction="row" spacing={2} sx={{ textAlign: 'center' }}>
              {!xsBreakpoint && i === 0 ? (
                <Avatar
                  src={resizeImage(p.avatarPublicId, 50, 50)}
                  variant="rounded"
                  sx={{
                    height: '50px',
                    width: '50px',
                    position: 'relative',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    filter:
                      isAdmin && FB[p.username] === 'b' ? 'blur(0.2rem)' : '',
                  }}
                />
              ) : null}
              <Stack justifyContent={'center'}>
                <Inputter
                  answer={answer}
                  currentLiveInput={liveInputs?.[p.profileId]}
                  fb={FB[p.username] === 'b'}
                  fuzzyScore={fuzzy?.[p.profileId]}
                  inputPlaceholder={p.username}
                  isAdmin={isAdmin}
                  mode={mode}
                  player={p}
                  locked={selectedPlayersLocked?.[p.profileId]}
                  questionIndex={questionIndex}
                  reverse={i === 1}
                  socket={socket}
                  timerLimitReached={timerLimitReached}
                  spectate={true}
                />
                {!xsBreakpoint ? (
                  <CorrectAnswer player={p} spectate={true}></CorrectAnswer>
                ) : null}
              </Stack>
              {i === 1 ? (
                <Avatar
                  src={resizeImage(p.avatarPublicId, 50, 50)}
                  variant="rounded"
                  sx={{
                    height: '50px',
                    width: '50px',
                    position: 'relative',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    filter:
                      isAdmin && FB[p.username] === 'b' ? 'blur(0.2rem)' : '',
                  }}
                />
              ) : null}
            </Stack>
          ))}
        </Stack>
      )}
    </>
  );
};
